<template>
  <div class="oauth-root">
    <div class="oauth-header">
      <div class="oauth-header-text">
        <div class="settings-section-title">OAuth Providers</div>
        <div class="settings-section-desc">
          Configure external login providers. Adding a provider automatically creates a spreadsheet to store OAuth user data.
        </div>
      </div>
      <button class="oauth-add-btn" @click="addProvider">
        <font-awesome-icon icon="plus" /> add provider
      </button>
    </div>

    <div class="oauth-list" v-if="providers.length">
      <div
        v-for="(provider, index) in providers"
        :key="provider.id"
        class="oauth-provider-card"
      >
        <div class="oauth-provider-header">
          <button type="button" class="oauth-provider-summary" :aria-expanded="expanded[index] ? 'true' : 'false'" @click="toggleExpand(index)">
            <font-awesome-icon :icon="expanded[index] ? 'chevron-down' : 'chevron-right'" class="oauth-expand-icon" />
            <span class="oauth-provider-type-badge" :class="'badge-' + provider.providerType">
              {{ provider.providerType }}
            </span>
            <span class="oauth-provider-name">
              {{ provider.displayName || 'New Provider' }}
            </span>
          </button>
          <button type="button" class="oauth-remove-btn" @click.stop="removeProvider(index)" aria-label="Remove provider">
            <font-awesome-icon icon="minus" />
          </button>
        </div>

        <div v-if="expanded[index]" class="oauth-provider-fields">
          <div class="oauth-field-row">
            <label class="oauth-label">Provider Type</label>
            <select class="oauth-select" v-model="provider.providerType" @change="onProviderTypeChange(provider)">
              <option value="google">Google</option>
              <option value="github">GitHub</option>
              <option value="microsoft">Microsoft</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Display Name</label>
            <input class="oauth-input" v-model="provider.displayName" placeholder="e.g. Sign in with Google" />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Client ID</label>
            <input class="oauth-input" v-model="provider.clientId" placeholder="OAuth client ID" />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Client Secret</label>
            <input class="oauth-input" v-model="provider.clientSecret" type="password" placeholder="OAuth client secret" />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Authorization URL</label>
            <input class="oauth-input" v-model="provider.authorizationUrl" placeholder="https://..." />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Token URL</label>
            <input class="oauth-input" v-model="provider.tokenUrl" placeholder="https://..." />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">User Info URL</label>
            <input class="oauth-input" v-model="provider.userInfoUrl" placeholder="https://..." />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Redirect URI</label>
            <input class="oauth-input" v-model="provider.redirectUri" placeholder="https://app.example.com/callback" />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Scopes</label>
            <input class="oauth-input" v-model="provider.scopes" placeholder="openid email profile" />
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Email Field</label>
            <input class="oauth-input" v-model="provider.emailField" placeholder="Field path in user info response (e.g. email)" />
            <div class="oauth-field-hint">JSON field name for the user's email in the provider's user info response</div>
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">User ID Field</label>
            <input class="oauth-input" v-model="provider.userIdField" placeholder="Field path in user info response (e.g. sub)" />
            <div class="oauth-field-hint">JSON field name for the user's unique ID in the provider's user info response</div>
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Display Name Field</label>
            <input class="oauth-input" v-model="provider.displayNameField" placeholder="Field path in user info response (e.g. name)" />
            <div class="oauth-field-hint">JSON field name for the user's display name in the provider's user info response</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="oauth-empty">
      No OAuth providers configured. Click <strong>+ add provider</strong> to get started.
    </div>

    <div class="oauth-footer" v-if="providers.length">
      <button
        class="save-btn"
        :class="{ 'save-btn-disabled': !hasChanges }"
        :disabled="!hasChanges || saving"
        @click="saveProviders"
      >
        <template v-if="saving">saving...</template>
        <template v-else-if="saved">saved!</template>
        <template v-else>save providers</template>
      </button>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'

const PROVIDER_TEMPLATES = {
  google: {
    displayName: 'Google',
    authorizationUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token',
    userInfoUrl: 'https://www.googleapis.com/oauth2/v3/userinfo',
    scopes: 'openid email profile',
    emailField: 'email',
    userIdField: 'sub',
    displayNameField: 'name'
  },
  github: {
    displayName: 'GitHub',
    authorizationUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token',
    userInfoUrl: 'https://api.github.com/user',
    scopes: 'read:user user:email',
    emailField: 'email',
    userIdField: 'id',
    displayNameField: 'login'
  },
  microsoft: {
    displayName: 'Microsoft',
    authorizationUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
    userInfoUrl: 'https://graph.microsoft.com/v1.0/me',
    scopes: 'openid email profile',
    emailField: 'mail',
    userIdField: 'id',
    displayNameField: 'displayName'
  },
  custom: {
    displayName: '',
    authorizationUrl: '',
    tokenUrl: '',
    userInfoUrl: '',
    scopes: '',
    emailField: 'email',
    userIdField: 'id',
    displayNameField: 'name'
  }
}

function generateId () {
  return 'oauth_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
}

function createProvider (providerType) {
  const template = PROVIDER_TEMPLATES[providerType] || PROVIDER_TEMPLATES.custom
  return {
    id: generateId(),
    providerType,
    displayName: template.displayName,
    clientId: '',
    clientSecret: '',
    authorizationUrl: template.authorizationUrl,
    tokenUrl: template.tokenUrl,
    userInfoUrl: template.userInfoUrl,
    scopes: template.scopes,
    emailField: template.emailField,
    userIdField: template.userIdField,
    displayNameField: template.displayNameField
  }
}

export default {
  name: 'SettingsOAuth',
  props: ['application'],
  data () {
    return {
      providers: [],
      savedSnapshot: '[]',
      expanded: [],
      saving: false,
      saved: false
    }
  },
  computed: {
    hasChanges () {
      return JSON.stringify(this.providers) !== this.savedSnapshot
    }
  },
  async mounted () {
    await this.loadProviders()
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {
    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  watch: {
    async application () {
      await this.loadProviders()
    }
  },
  methods: {
    async applyDesignerChange (change) {
      const keys = change && change.keys || []
      if (!keys.includes('settings:oauth') && !keys.includes('settings') && !keys.includes('application')) return
      if (this.hasChanges) {
        raiseDesignerWarning(change, keys.includes('application') ? 'this application' : 'sign-in providers')
        return
      }
      await this.loadProviders()
    },
    async loadProviders () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          headers: { 'application-unique-title': this.application }
        })

        if (res.data && res.data.oauthProviders) {
          this.providers = res.data.oauthProviders
        } else {
          this.providers = []
        }

        this.savedSnapshot = JSON.stringify(this.providers)
        this.expanded = this.providers.map(() => false)
      } catch (err) {
        console.log('failed to load oauth providers', err)
      }
    },
    addProvider () {
      const provider = createProvider('google')
      this.providers.push(provider)
      this.expanded.push(true)
    },
    removeProvider (index) {
      if (!confirm('Remove this OAuth provider?')) return
      this.providers.splice(index, 1)
      this.expanded.splice(index, 1)
    },
    toggleExpand (index) {
      this.expanded[index] = !this.expanded[index]
      this.expanded = [...this.expanded]
    },
    onProviderTypeChange (provider) {
      const template = PROVIDER_TEMPLATES[provider.providerType] || PROVIDER_TEMPLATES.custom
      provider.displayName = template.displayName
      provider.authorizationUrl = template.authorizationUrl
      provider.tokenUrl = template.tokenUrl
      provider.userInfoUrl = template.userInfoUrl
      provider.scopes = template.scopes
      provider.emailField = template.emailField
      provider.userIdField = template.userIdField
      provider.displayNameField = template.displayNameField
    },
    async saveProviders () {
      if (!this.hasChanges) return

      this.saving = true
      this.saved = false

      try {
        await axios.put(
          `${VUE_APP_PROXY_SERVER_URL}/settings`,
          { oauthProviders: this.providers },
          { headers: { 'application-unique-title': this.application } }
        )

        this.savedSnapshot = JSON.stringify(this.providers)
        this.saved = true

        setTimeout(() => { this.saved = false }, 2000)
      } catch (err) {
        console.log('failed to save oauth providers', err)
        alert('Failed to save OAuth providers')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>

.oauth-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 102px);
  overflow-y: auto;
  padding: 16px 24px;
}

.oauth-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.settings-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 2px;
}

.settings-section-desc {
  font-size: 0.75rem;
  color: #707070;
  max-width: 480px;
}

.oauth-add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 5px;
  border: 1px solid #3a3a37;
  background: transparent;
  color: #a0a09e;
  font-size: 0.78rem;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.oauth-add-btn:hover {
  border-color: #9ad12b;
  color: #e8e8e6;
  background: rgba(154, 209, 43, 0.06);
}

/* ── Provider list ── */
.oauth-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.oauth-provider-card {
  border: 1px solid #2a2a27;
  border-radius: 6px;
  background: #141412;
  overflow: hidden;
}

.oauth-provider-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  transition: background 0.1s;
}

.oauth-provider-header:hover {
  background: #1a1a17;
}

.oauth-provider-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}

.oauth-expand-icon {
  font-size: 0.65rem;
  color: #a8a8a4;
  width: 10px;
}

.oauth-provider-type-badge {
  font-size: 0.65rem;
  padding: 2px 7px;
  border-radius: 3px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge-google { color: #4285f4; background: rgba(66, 133, 244, 0.12); }
.badge-github { color: #c9d1d9; background: rgba(201, 209, 217, 0.12); }
.badge-microsoft { color: #00a4ef; background: rgba(0, 164, 239, 0.12); }
.badge-custom { color: #9ad12b; background: rgba(154, 209, 43, 0.12); }

.oauth-provider-name {
  font-size: 0.82rem;
  color: #c0c0be;
}

.oauth-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: transparent;
  color: #a8a8a4;
  font-size: 0.7rem;
  cursor: pointer;
  transition: color 0.12s, background 0.12s, border-color 0.12s;
}

.oauth-remove-btn:hover {
  color: #e05050;
  background: rgba(224, 80, 80, 0.08);
  border-color: rgba(224, 80, 80, 0.2);
}

/* ── Provider fields ── */
.oauth-provider-fields {
  padding: 4px 14px 14px;
  border-top: 1px solid #2a2a27;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.oauth-field-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.oauth-label {
  font-size: 0.72rem;
  color: #707070;
  font-weight: 500;
}

.oauth-input,
.oauth-select {
  width: 100%;
  max-width: 500px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #2a2a27;
  background: #0f0f0d;
  color: #e8e8e6;
  font-size: 0.8rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.12s;
}

.oauth-input:focus,
.oauth-select:focus {
  border-color: #9ad12b;
}

.oauth-input::placeholder {
  color: #3a3a37;
}

.oauth-select option {
  background: #0f0f0d;
  color: #e8e8e6;
}

.oauth-field-hint {
  font-size: 0.68rem;
  color: #a8a8a4;
}

/* ── Empty state ── */
.oauth-empty {
  text-align: center;
  padding: 48px 20px;
  color: #a8a8a4;
  font-size: 0.82rem;
}

.oauth-empty strong {
  color: #707070;
}

/* ── Footer ── */
.oauth-footer {
  margin-top: 16px;
  max-width: 500px;
}

.save-btn {
  width: 100%;
  padding: 7px 0;
  border-radius: 5px;
  border: none;
  background-color: #9ad12b;
  color: #0f0f0d;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.12s, background 0.12s;
}

.save-btn:hover {
  background-color: #b0e840;
}

.save-btn-disabled {
  opacity: 0.35;
  cursor: default;
  background-color: #3a3a37;
  color: #707070;
}

.save-btn-disabled:hover {
  background-color: #3a3a37;
}

</style>
